import React, { useMemo } from 'react'
import { any, prop } from 'lodash/fp'
import { useTranslation } from 'react-i18next'

import { getPricingTypeChoices } from '@/entities/assortment'
import {
	createChoices,
	toAdaptedSchema,
	useColumnsSchemaQuery,
} from '@/entities/column'
import { useBrandsQuery, useCategoriesQuery } from '@/entities/core'
import {
	useOptimizationGroupsQuery,
	useSalesEntitiesQuery,
} from '@/entities/optimization-group'
import {
	getAlertAnnotationDataChoices,
	usePricingAlertsAnnotationsQuery,
} from '@/entities/price-alert'
import { usePricingCampaignsQuery } from '@/entities/pricing-campaign'

import RHFQueryBuilder from '@/shared/components/HookForm/RHFQueryBuilder'
import { HiddenOnContext } from '@/shared/generated'

const excludeSchemaFields = ['optimization_group_status']

export const QueryFiltersField = ({
	name,
	label,
}: {
	name: string
	label: string
}) => {
	const { t } = useTranslation()

	/**
	 * Queries
	 */
	const columnsSchemaQuery = useColumnsSchemaQuery(HiddenOnContext.Assortment)
	const { data: sales_entity, ...salesEntitiesQuery } = useSalesEntitiesQuery()
	const { data: category, ...categoriesQuery } = useCategoriesQuery()
	const { data: brand, ...brandsQuery } = useBrandsQuery({})
	const { data: optimization_group, ...optimizationGroupsQuery } =
		useOptimizationGroupsQuery()
	const { data: pricing_campaign, ...pricingCampaignsQuery } =
		usePricingCampaignsQuery()
	const { data: alert, ...pricingAlertsAnnotationsQuery } =
		usePricingAlertsAnnotationsQuery({
			select: (data) => getAlertAnnotationDataChoices(data, t),
		})
	const entities = useMemo(
		() => ({
			sales_entity: sales_entity?.sales_entities || [],
			category: category || [],
			brand: brand || [],
			optimization_group: optimization_group || [],
			pricing_campaign: pricing_campaign || [],
			alert: alert || [],
		}),
		[sales_entity, category, brand, optimization_group, pricing_campaign, alert]
	)
	const dataChoices = useMemo(() => {
		const schema = columnsSchemaQuery.data || []

		return {
			pricing_type: getPricingTypeChoices(t),
			...createChoices({
				schema,
				entities,
			}),
		}
	}, [entities, columnsSchemaQuery.data, t])

	const fields = useMemo(
		() =>
			(columnsSchemaQuery.data || [])
				.filter(({ id }) => !excludeSchemaFields.includes(id))
				.map((schema) => toAdaptedSchema(schema, t)),
		[columnsSchemaQuery.data, t]
	)
	const isLoading = any(prop('isLoading'), [
		columnsSchemaQuery,
		salesEntitiesQuery,
		categoriesQuery,
		brandsQuery,
		optimizationGroupsQuery,
		pricingCampaignsQuery,
		pricingAlertsAnnotationsQuery,
	])

	return (
		<RHFQueryBuilder
			name={name}
			label={label}
			fields={fields}
			dataChoices={dataChoices}
			isLoading={isLoading}
			enableReferencing
		/>
	)
}
